import { catchError, from, map, Observable, tap } from 'rxjs';
import { AxiosError, AxiosResponse } from 'axios';
import { setEntities } from '@ngneat/elf-entities';
import { GridSortItem } from '@mui/x-data-grid-pro';
import { setPage, updatePaginationData } from '@ngneat/elf-pagination';

import APIAxios, { APIRoutes } from '@api/axios.api';
import SnackError from '@utils/error.utils';
import { Pagination } from '@store/common/pagination.model';
import { TableFilter } from '@store/common/tableFilter.model';
import { History, HistoryFilters, HistoryPagination } from './history.model';
import { historiesStore } from './history.store';
import { Array } from '@utils/extensions/array.extension';
import { getHistoriesDataSource } from './history.requests';
import { TableConfig } from '@constants/table.constants';

export class HistoryService {
  store = historiesStore;

  setSort = (sort: GridSortItem) => this.store.update((state) => ({ ...state, sort }));
  setPagination = (name: string) => (value: number) =>
    this.store.update((state) => ({
      ...state,
      tablePagination: {
        ...state.tablePagination,
        [name]: value,
      },
    }));

  resetFilters = (authorId?: string) =>
    this.store.update((state) => ({
      ...state,
      filters: { search: '', tableFilters: HistoryFilters.initTableFilters(authorId) },
    }));
  setFilters = (name: string) => (value: any) =>
    this.store.update((state) => ({
      ...state,
      filters: {
        ...state.filters,
        [name]: value,
      },
    }));

  searchHistories = (search?: string, authorIds: (string | number)[] = []): Observable<HistoryPagination> => {
    return from(
      APIAxios({
        ...APIRoutes.GETHistories(),
        params: {
          authorIds: Array.formatJson(Array.mapNotNull(authorIds)),
          searchString: search || undefined,
          page: 1,
          limit: TableConfig.pagination,
        },
      })
    ).pipe(
      catchError((err: AxiosError) => {
        throw new SnackError((err.response as any)?.data?.message, 'error');
      }),
      map((response: AxiosResponse<HistoryPagination>) => {
        return response.data;
      })
    );
  };

  getHistories = (): Observable<HistoryPagination> => {
    const {
      filters: { search, tableFilters, fromDate, toDate },
      sort,
      tablePagination,
    } = this.store.getValue();
    const requestFilters = TableFilter.toRequestFilters(tableFilters);

    return from(
      APIAxios({
        ...APIRoutes.GETHistories(),
        params: {
          ...requestFilters,
          from: fromDate?.toISOString() || undefined,
          to: toDate?.toISOString() || undefined,
          searchString: search || undefined,
          page: tablePagination.page,
          limit: tablePagination.limit,
          sortBy: sort.field,
          orderBy: sort.sort?.toLocaleUpperCase(),
        },
      })
    ).pipe(
      catchError((err: AxiosError) => {
        throw new SnackError((err.response as any)?.data?.message, 'error');
      }),
      map((response: AxiosResponse<HistoryPagination>) => {
        return response.data;
      }),
      tap((historyPagination) => {
        const paginationData = Pagination.toPaginationData(historyPagination, tablePagination.page);

        this.store.update(
          setEntities(historyPagination.data),
          updatePaginationData(paginationData),
          setPage(
            paginationData.currentPage,
            historyPagination.data.map((el) => el.id)
          ),
          getHistoriesDataSource.setSuccess()
        );
      }),
      getHistoriesDataSource.trackRequestStatus()
    );
  };

  getHistory = (historyId: string): Observable<History> => {
    return from(
      APIAxios({
        ...APIRoutes.GETHistory(historyId),
      })
    ).pipe(
      catchError((err: AxiosError) => {
        throw new SnackError((err.response as any)?.data?.message, 'error');
      }),
      map((response: AxiosResponse<History>) => {
        return response.data;
      }),
      tap((history) => {
        this.store.update(
          (state) => ({
            ...state,
            history,
          }),
          getHistoriesDataSource.setSuccess()
        );
      }),
      getHistoriesDataSource.trackRequestStatus()
    );
  };

  getHistoryByResourceId = (historyId: string, tableName: string): Observable<History[]> => {
    return from(
      APIAxios({
        ...APIRoutes.GETHistoryByResourceId(historyId),
        params: {
          tableName,
        }
      })
    ).pipe(
      catchError((err: AxiosError) => {
        throw new SnackError((err.response as any)?.data?.message, 'error');
      }),
      map((response: AxiosResponse<History[]>) => {
        return response.data;
      }),
      tap((history) => {
        this.store.update(setEntities(history));
        getHistoriesDataSource.setSuccess();
      }),
      getHistoriesDataSource.trackRequestStatus()
    );
  };
}

export const historyService = new HistoryService();
